import type { ValueFormatterParams } from 'ag-grid-community';
import { parseLocalizedNumber, round, round2 } from '@callmart/types';

/**
 * Number parsing is pinned to German (the shared parser's default), matching the backend.
 *
 * It used to pass `navigator.language`, which made an ambiguous cell like "1.000" parse as 1000 on a
 * German machine and 1 on an en-US one — silently, with no error, in every grid that accepts Excel
 * paste (CALL-794). The app displays German-formatted numbers and is fed German Excel regardless of
 * the browser, so the browser is the wrong thing to ask. Budget already pinned 'de' for the same
 * reason (CALL-744 #12).
 *
 * The UI language is likewise not consulted: switching the app language must not change how numbers
 * are parsed (CALL-759). Callers needing another locale can pass one explicitly.
 */
export { parseLocalizedNumber };

export const parseStringToFloat = (value: unknown): unknown => {
    if (typeof value === 'string') {
        const parsedValue = parseLocalizedNumber(value);
        if (parsedValue !== null) return parsedValue;
    }

    return value;
};

export const isNumberDecimal = (value: number): boolean => {
    return value % 1 !== 0;
};

/**
 * Display formatting is pinned to German too, for the whole app (CALL-796).
 *
 * Numbers used to render three different ways depending on the module: de-DE in Budget, de-DE via
 * the legacy `stringToNumberFormatter` chain in StrategicVolume/CapacityReport/WFM, and unformatted
 * everywhere else. Two grids even followed the *browser* locale, so the same figure read `1,000` on
 * an en-US machine and `1.000` on a German one.
 *
 * Pinning display to the same locale as the parser above is what makes a cell round-trip: what a
 * grid shows can be copied straight back into it. The UI language is deliberately not consulted —
 * CALL-759 keeps dates and number formats German in both languages.
 *
 * CALL-745 perf: these are module-level, shared instances. Constructing Intl.NumberFormat is
 * relatively expensive and ag-grid runs valueFormatter per visible cell.
 */
export const currencyFormatter = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

/**
 * CALL-813: euro cells that are READ-ONLY TOTALS — the Budget Overview's computed sums — round to
 * whole euros. Cents on a six-figure monthly total are noise the board never reports.
 *
 * BOTH fraction-digit bounds have to be set: the EUR currency default for the minimum is 2, and a
 * minimum above the maximum makes Intl throw a RangeError, so `maximumFractionDigits: 0` alone
 * cannot work.
 *
 * Do NOT hand this raw values — go through formatWholeEuro, which normalises them first. Intl's
 * rounding is close to the shared `round()` but not identical to it: it keeps a negative sign on a
 * value that rounds to zero ("-0 €"), and it decides ties on the float representation alone.
 *
 * Deliberately NOT used on an editable cell: see formatWholeEuro.
 */
export const wholeEuroFormatter = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

export const priceFormatter = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
});

export const integerFormatter = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });

/** Plain German decimals, up to 2 places, signed. Used for percent cells (with a " %" suffix) and
 *  for unitless scenario figures like the driver deltas. */
export const decimalFormatter = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

/** Always exactly 2 decimals, no unit — service factors, reduction percentages, contact rates.
 *  These read as measurements, so "1,50" is wanted where decimalFormatter would give "1,5". */
const fixedDecimalFormatter = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

/**
 * Coerce anything a grid cell may hold into a number, or null for "nothing to show".
 *
 * Strings go through the German-pinned parser rather than `Number()`, so a value that has already
 * been formatted once — or came from a DB decimal column as "1234.56" — still formats correctly.
 * The legacy `stringToNumberFormatter` hand-rolled this by replacing the first comma with a dot,
 * which turned "1.234,56" into the unparseable "1.234.56" and rendered NaN.
 */
const toNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'string') return parseLocalizedNumber(value);

    const num = Number(value);
    return Number.isFinite(num) ? num : null;
};

/** General-purpose number cell: thousands separators, up to 2 decimals. Empty for a blank cell. */
export const formatNumber = (value: unknown): string => {
    const num = toNumber(value);
    return num === null ? '' : decimalFormatter.format(num);
};

/** Whole numbers — call volumes, bookings, contacts. Empty for a blank cell. */
export const formatInteger = (value: unknown): string => {
    const num = toNumber(value);
    return num === null ? '' : integerFormatter.format(num);
};

/** Exactly 2 decimals, no unit. Empty for a blank cell. */
export const formatFixed2 = (value: unknown): string => {
    const num = toNumber(value);
    return num === null ? '' : fixedDecimalFormatter.format(num);
};

export const formatCurrency = (value: unknown): string => {
    const num = toNumber(value);
    return num === null ? '' : currencyFormatter.format(num);
};

/**
 * Whole euros, no cents — for a READ-ONLY euro total only (CALL-813).
 *
 * Never put this on an editable cell. The CALL-794/796 contract is that whatever a grid displays can
 * be typed or pasted straight back into it and parsed by the German-pinned parser; a cell holding
 * 1234,56 that renders "1.235 €" silently destroys the cents the moment the user re-enters it.
 * Editable euro cells keep formatCurrency's two decimals.
 *
 * Rounding runs through the shared `round`/`round2` rather than being left to Intl, for two reasons:
 *   - Intl keeps the sign on a value that rounds to zero, so a Delta of -0,40 rendered "-0 €".
 *     `round` normalises any rounded-zero to 0.
 *   - Money reaches this function as a float SUM, which can land just under a tie: the three cent
 *     values 66,44 + 89,02 + 41,04 add up to 196.49999999999997, which Intl floors to "196 €" when
 *     the true total 196,50 must round to 197. Snapping to cent precision first recovers the tie.
 *     `round(x, 0)` alone cannot: its epsilon nudge stops working above magnitude ~1, as its own
 *     doc comment says.
 */
export const formatWholeEuro = (value: unknown): string => {
    const num = wholeEuroValue(value);
    return num === null ? '' : wholeEuroFormatter.format(num);
};

/**
 * The number `formatWholeEuro` will render, or null for a cell it renders empty.
 *
 * For code that must AGREE with the digits on screen — the Overview's Delta colour rules, which paint
 * on the sign. Reading the raw value there painted a red "0 €" for -0,40, and coercing with `Number()`
 * instead of the German-pinned `toNumber` would disagree again the moment a value arrives as a string.
 * One function, so the colour and the text cannot drift apart.
 */
export const wholeEuroValue = (value: unknown): number | null => {
    const num = toNumber(value);
    return num === null ? null : round(round2(num), 0);
};

/**
 * Euros with cents ONLY when the figure actually has them: "200.000 €", but "200.000,43 €".
 *
 * For the Overview's hand-typed Ist-Budget row (CALL-813). A budget is entered in whole thousands,
 * so pinning two decimals there just prints a ",00" nobody needs — while rounding it outright would
 * hide a cent figure somebody did type. All-or-nothing rather than an Intl min 0 / max 2 formatter,
 * because that renders 1234.5 as "1.234,5 €", which reads wrong for money.
 *
 * round2 first, so float noise (200000.00000000003) doesn't read as "has cents".
 */
export const formatEuroOptionalCents = (value: unknown): string => {
    const num = toNumber(value);
    if (num === null) return '';
    const cents = round2(num);
    return isNumberDecimal(cents) ? currencyFormatter.format(cents) : formatWholeEuro(cents);
};

/** Percent cells across the app: "2,5 %", empty for a blank cell. */
export const formatPercent = (value: unknown): string => {
    const num = toNumber(value);
    return num === null ? '' : `${decimalFormatter.format(num)} %`;
};

/** Percent with exactly 2 decimals: "12,34 %". */
export const formatPercentFixed2 = (value: unknown): string => {
    const num = toNumber(value);
    return num === null ? '' : `${fixedDecimalFormatter.format(num)} %`;
};

/** Plain signed decimal (no unit) — the driver-delta cells. Empty for a blank cell. */
export const formatDecimal = formatNumber;

/** ag-grid valueFormatter adapters, so no column has to write its own arrow function. */
export const numberValueFormatter = ({ value }: ValueFormatterParams): string => formatNumber(value);
export const integerValueFormatter = ({ value }: ValueFormatterParams): string => formatInteger(value);
export const fixed2ValueFormatter = ({ value }: ValueFormatterParams): string => formatFixed2(value);
export const currencyValueFormatter = ({ value }: ValueFormatterParams): string => formatCurrency(value);
export const wholeEuroValueFormatter = ({ value }: ValueFormatterParams): string => formatWholeEuro(value);
